import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Camera, X, FileUp, Download, CheckCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { getSignedFileUrl } from "@/lib/storage-utils";
import { ALL_PERMISSIONS, ROLE_PRESETS } from "@/lib/permissions";

const staffSchema = z.object({
  full_name: z.string().trim().min(2, "الاسم يجب أن يكون حرفين على الأقل").max(100),
  role: z.string().min(1, "يرجى اختيار الوظيفة"),
  phone: z.string().trim().max(20).optional().or(z.literal("")),
  salary_type: z.enum(["monthly", "per_session", "percentage"]).default("monthly"),
  salary: z.string().optional().or(z.literal("")),
  session_price: z.string().optional().or(z.literal("")),
  commission_percentage: z.string().optional().or(z.literal("")),
  allowances: z.string().optional().or(z.literal("")),
  bonuses: z.string().optional().or(z.literal("")),
  deductions: z.string().optional().or(z.literal("")),
});

type StaffFormValues = z.infer<typeof staffSchema>;

interface EditStaffDialogProps {
  staff: { id: string; full_name: string; role: string; phone: string | null; salary: number | null; salary_type?: string; session_price?: number | null; commission_percentage?: number | null; allowances?: number | null; bonuses?: number | null; deductions?: number | null; photo_url?: string | null; user_id?: string | null };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const EditStaffDialog = ({ staff, open, onOpenChange, onSuccess }: EditStaffDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [existingDocs, setExistingDocs] = useState<any[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const { profile } = useAuth();

  const form = useForm<StaffFormValues>({
    resolver: zodResolver(staffSchema),
    defaultValues: {
      full_name: staff.full_name,
      role: staff.role,
      phone: staff.phone || "",
      salary_type: (staff.salary_type as any) || "monthly",
      salary: staff.salary?.toString() || "",
      session_price: staff.session_price?.toString() || "0",
      commission_percentage: staff.commission_percentage?.toString() || "0",
      allowances: staff.allowances?.toString() || "0",
      bonuses: staff.bonuses?.toString() || "0",
      deductions: staff.deductions?.toString() || "0",
    },
  });

  const salaryType = form.watch("salary_type");

  useEffect(() => {
    form.reset({
      full_name: staff.full_name,
      role: staff.role,
      phone: staff.phone || "",
      salary_type: (staff.salary_type as any) || "monthly",
      salary: staff.salary?.toString() || "",
      session_price: staff.session_price?.toString() || "0",
      commission_percentage: staff.commission_percentage?.toString() || "0",
      allowances: staff.allowances?.toString() || "0",
      bonuses: staff.bonuses?.toString() || "0",
      deductions: staff.deductions?.toString() || "0",
    });
    setPhotoFile(null);
    setDocFiles([]);

    // Load existing photo
    if (staff.photo_url) {
      getSignedFileUrl("staff-photos", staff.photo_url).then(url => setPhotoPreview(url));
    } else {
      setPhotoPreview(null);
    }

    // Load existing documents
    supabase.from("staff_documents").select("*").eq("staff_id", staff.id).then(({ data }) => {
      setExistingDocs(data || []);
    });

    // Load existing permissions
    supabase.from("staff_permissions").select("permission").eq("staff_id", staff.id).then(({ data }) => {
      setSelectedPermissions((data || []).map((p: any) => p.permission));
    });
  }, [staff, form]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { toast.error("حجم الصورة يجب أن يكون أقل من 5 ميجابايت"); return; }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.size <= 10 * 1024 * 1024);
    if (valid.length < files.length) toast.error("تم تجاهل ملفات أكبر من 10 ميجابايت");
    setDocFiles(prev => [...prev, ...valid]);
  };

  const downloadDoc = async (doc: any) => {
    const url = await getSignedFileUrl("staff-documents", doc.file_url);
    if (url) window.open(url, "_blank");
  };

  const togglePermission = (key: string) => {
    setSelectedPermissions(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const applyPreset = (presetKey: string) => {
    setSelectedPermissions(ROLE_PRESETS[presetKey] || []);
  };

  const onSubmit = async (values: StaffFormValues) => {
    setLoading(true);
    try {
      let photo_url = staff.photo_url;

      if (photoFile && profile?.nursery_id) {
        const ext = photoFile.name.split(".").pop();
        const path = `${profile.nursery_id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("staff-photos").upload(path, photoFile);
        if (upErr) throw new Error("خطأ في رفع الصورة");
        photo_url = path;
      }

      const { error } = await supabase.from("staff").update({
        full_name: values.full_name,
        role: values.role,
        phone: values.phone || null,
        salary_type: values.salary_type,
        salary: values.salary ? parseFloat(values.salary) : null,
        session_price: values.session_price ? parseFloat(values.session_price) : 0,
        commission_percentage: values.commission_percentage ? parseFloat(values.commission_percentage) : 0,
        allowances: values.allowances ? parseFloat(values.allowances) : 0,
        bonuses: values.bonuses ? parseFloat(values.bonuses) : 0,
        deductions: values.deductions ? parseFloat(values.deductions) : 0,
        photo_url,
      } as any).eq("id", staff.id);

      if (error) throw new Error("خطأ في تعديل الموظف: " + error.message);

      // Upload new documents
      if (docFiles.length > 0 && profile?.nursery_id) {
        for (const doc of docFiles) {
          const ext = doc.name.split(".").pop();
          const path = `${profile.nursery_id}/${staff.id}/${crypto.randomUUID()}.${ext}`;
          const { error: docUpErr } = await supabase.storage.from("staff-documents").upload(path, doc);
          if (docUpErr) continue;
          await supabase.from("staff_documents").insert({
            staff_id: staff.id,
            nursery_id: profile.nursery_id,
            file_name: doc.name,
            file_url: path,
            file_type: doc.type,
            file_size: doc.size,
          });
        }
      }

      // Update permissions if staff has account
      if (staff.user_id && profile?.nursery_id) {
        // Delete old permissions
        await supabase.from("staff_permissions").delete().eq("staff_id", staff.id);
        // Insert new permissions
        if (selectedPermissions.length > 0) {
          await supabase.from("staff_permissions").insert(
            selectedPermissions.map(p => ({ staff_id: staff.id, nursery_id: profile.nursery_id!, permission: p }))
          );
        }
      }

      toast.success("تم تعديل بيانات الموظف بنجاح!");
      onOpenChange(false);
      onSuccess();
    } catch (err: any) {
      toast.error(err.message || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>تعديل بيانات الموظف</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Photo */}
            <div className="flex flex-col items-center gap-2">
              <div className="relative cursor-pointer" onClick={() => photoInputRef.current?.click()}>
                <Avatar className="w-20 h-20 border-2 border-dashed border-muted-foreground/30">
                  <AvatarImage src={photoPreview || ""} />
                  <AvatarFallback className="bg-muted"><Camera className="w-6 h-6 text-muted-foreground" /></AvatarFallback>
                </Avatar>
              </div>
              <span className="text-xs text-muted-foreground">اضغط لتغيير الصورة</span>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
            </div>

            <FormField control={form.control} name="full_name" render={({ field }) => (
              <FormItem><FormLabel>الاسم الكامل *</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />

            <FormField control={form.control} name="role" render={({ field }) => (
              <FormItem>
                <FormLabel>الوظيفة *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="teacher">مدرس/مدرسة</SelectItem>
                    <SelectItem value="supervisor">مشرف/مشرفة</SelectItem>
                    <SelectItem value="admin">إداري/إدارية</SelectItem>
                    <SelectItem value="driver">سائق</SelectItem>
                    <SelectItem value="cleaner">عامل نظافة</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="phone" render={({ field }) => (
              <FormItem><FormLabel>رقم الهاتف</FormLabel><FormControl><Input dir="ltr" {...field} /></FormControl><FormMessage /></FormItem>
            )} />

            {/* Salary Type Selection */}
            <div className="border rounded-lg p-3 space-y-3">
              <label className="text-sm font-medium text-foreground">نوع الراتب</label>
              <FormField control={form.control} name="salary_type" render={({ field }) => (
                <FormItem>
                  <div className="flex gap-2">
                    {[
                      { value: "monthly", label: "شهري", icon: "💰" },
                      { value: "per_session", label: "بالحصة", icon: "🎓" },
                      { value: "percentage", label: "نسبة", icon: "📊" },
                    ].map(opt => (
                      <Button
                        key={opt.value}
                        type="button"
                        size="sm"
                        variant={field.value === opt.value ? "default" : "outline"}
                        className="flex-1 gap-1"
                        onClick={() => field.onChange(opt.value)}
                      >
                        {opt.icon} {opt.label}
                      </Button>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )} />

              {salaryType === "monthly" && (
                <FormField control={form.control} name="salary" render={({ field }) => (
                  <FormItem><FormLabel>الراتب الشهري</FormLabel><FormControl><Input type="number" dir="ltr" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
              )}

              {salaryType === "per_session" && (
                <FormField control={form.control} name="session_price" render={({ field }) => (
                  <FormItem>
                    <FormLabel>سعر الحصة الواحدة</FormLabel>
                    <FormControl><Input type="number" dir="ltr" {...field} /></FormControl>
                    <p className="text-xs text-muted-foreground">سيتم حساب الراتب: عدد الحصص × سعر الحصة</p>
                    <FormMessage />
                  </FormItem>
                )} />
              )}

              {salaryType === "percentage" && (
                <FormField control={form.control} name="commission_percentage" render={({ field }) => (
                  <FormItem>
                    <FormLabel>النسبة المئوية %</FormLabel>
                    <FormControl><Input type="number" dir="ltr" min="0" max="100" {...field} /></FormControl>
                    <p className="text-xs text-muted-foreground">سيتم حساب الراتب: إجمالي الإيرادات × النسبة</p>
                    <FormMessage />
                  </FormItem>
                )} />
              )}
            </div>

            {/* Salary Components */}
            <div className="border rounded-lg p-3 space-y-3">
              <label className="text-sm font-medium text-foreground">مكونات الراتب</label>
              <div className="grid grid-cols-3 gap-3">
                <FormField control={form.control} name="allowances" render={({ field }) => (
                  <FormItem><FormLabel className="text-xs">البدلات</FormLabel><FormControl><Input type="number" dir="ltr" {...field} /></FormControl></FormItem>
                )} />
                <FormField control={form.control} name="bonuses" render={({ field }) => (
                  <FormItem><FormLabel className="text-xs">العلاوات</FormLabel><FormControl><Input type="number" dir="ltr" {...field} /></FormControl></FormItem>
                )} />
                <FormField control={form.control} name="deductions" render={({ field }) => (
                  <FormItem><FormLabel className="text-xs">الخصومات</FormLabel><FormControl><Input type="number" dir="ltr" {...field} /></FormControl></FormItem>
                )} />
              </div>
            </div>

            {/* Permissions - only show if staff has account */}
            {staff.user_id && (
              <div className="border rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">صلاحيات الوصول</label>
                  <div className="flex gap-1">
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedPermissions(ALL_PERMISSIONS.map(p => p.key))}>
                      <CheckCheck className="w-3 h-3 ml-1" /> الكل
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedPermissions([])}>
                      <RotateCcw className="w-3 h-3 ml-1" /> مسح
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(ROLE_PRESETS).map(([key, _]) => (
                    <Button key={key} type="button" variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => applyPreset(key)}>
                      {key === "admin" ? "إداري" : key === "teacher" ? "مدرس" : key === "supervisor" ? "مشرف" : "محاسب"}
                    </Button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
                  {ALL_PERMISSIONS.map(p => {
                    const Icon = p.icon;
                    return (
                      <label key={p.key} className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-muted cursor-pointer">
                        <Checkbox checked={selectedPermissions.includes(p.key)} onCheckedChange={() => togglePermission(p.key)} />
                        <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span>{p.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {existingDocs.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">المستندات المرفقة</label>
                <div className="space-y-1">
                  {existingDocs.map((doc: any) => (
                    <div key={doc.id} className="flex items-center justify-between text-xs bg-muted rounded px-2 py-1.5">
                      <span className="truncate flex-1">{doc.file_name}</span>
                      <button type="button" onClick={() => downloadDoc(doc)}><Download className="w-3.5 h-3.5 text-primary" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Documents */}
            <div className="space-y-2">
              <label className="text-sm font-medium">إضافة مستندات جديدة</label>
              <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => docInputRef.current?.click()}>
                <FileUp className="w-4 h-4 ml-2" />
                رفع مستندات
              </Button>
              <input ref={docInputRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={handleDocChange} />
              {docFiles.length > 0 && (
                <div className="space-y-1">
                  {docFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-muted rounded px-2 py-1">
                      <span className="truncate flex-1">{f.name}</span>
                      <button type="button" onClick={() => setDocFiles(prev => prev.filter((_, idx) => idx !== i))}><X className="w-3 h-3 text-muted-foreground hover:text-destructive" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 gradient-hero border-0" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التعديلات"}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditStaffDialog;
